import React, { useState, useRef, useEffect } from 'react';
import { X, Plus } from 'lucide-react';

interface GroupCardProps {
  headerLabel: string;
  triggers: string[];
  onRemoveTrigger: (key: string) => void;
  onAddTrigger: (key: string, value: string) => void;
  chipColor: string;       // e.g. 'green' or 'blue'
  addPlaceholder: string;  // e.g. "Type alias..." or "Type misheard word..."
}

const chipColors: Record<string, string> = {
  green: 'bg-green-100 text-green-700 border-green-200',
  blue: 'bg-blue-100 text-blue-700 border-blue-200',
};

const chipHoverColors: Record<string, string> = {
  green: 'hover:text-green-900',
  blue: 'hover:text-blue-900',
};

const GroupCard: React.FC<GroupCardProps> = ({
  headerLabel,
  triggers,
  onRemoveTrigger,
  onAddTrigger,
  chipColor,
  addPlaceholder,
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isAdding && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isAdding]);

  const handleAdd = () => {
    const trimmed = inputValue.trim();
    if (trimmed) {
      onAddTrigger(trimmed.toLowerCase(), headerLabel);
      setInputValue('');
      // Keep input open for rapid entry
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleAdd();
    } else if (e.key === 'Escape') {
      setIsAdding(false);
      setInputValue('');
    }
  };

  const handleBlur = () => {
    if (inputValue.trim()) {
      handleAdd();
    }
    // Small delay so click on [+] doesn't immediately close
    setTimeout(() => setIsAdding(false), 150);
  };

  const colorClass = chipColors[chipColor] || chipColors.green;
  const hoverClass = chipHoverColors[chipColor] || chipHoverColors.green;

  return (
    <div className="bg-gray-50 rounded-lg border border-gray-200 p-3 mb-2">
      <div className="text-sm font-semibold text-gray-800 mb-2">{headerLabel}</div>
      <div className="flex flex-wrap items-center gap-1.5">
        {triggers.map((trigger) => (
          <span
            key={trigger}
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${colorClass}`}
          >
            {trigger}
            <button
              onClick={() => onRemoveTrigger(trigger)}
              className={`ml-0.5 ${hoverClass} cursor-pointer`}
              title={`Remove "${trigger}"`}
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        {isAdding ? (
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            placeholder={addPlaceholder}
            className="text-xs px-2.5 py-1 rounded-full border border-gray-300 focus:ring-1 focus:ring-purple-400 focus:border-purple-400 outline-none min-w-[120px] max-w-[220px]"
          />
        ) : (
          <button
            onClick={() => setIsAdding(true)}
            className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-200 hover:bg-gray-300 text-gray-500 transition-colors"
            title="Add trigger"
          >
            <Plus className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
};

export default GroupCard;
